import React, { useState } from 'react';
import { useCreateLocation } from './useCreateLocation';
import { LocationEditSaveProps } from './LocationEditTypes';
import { useUpdateLocation } from './useUpdateLocation';
import { useCopyLocation } from './useCopyLocation';
import { useRequestCitiesCountries, areCoordinatesClose } from '~/requests';
import { ToastVariant, useToastMessage, useGetCoordinates } from '~/Hooks';
import { generateUUID } from '~/Helpers/ReactQuery.helper';
import { useTranslation, Trans } from 'react-i18next';

export const useLocationEditSave = ({
  currentLocation,
  isCopying,
  isSubmitDisabled,
  hasValidationErrors,
  setShowFormErrors,
  setIsSubmitDisabled,
  setIsLoading,
}: LocationEditSaveProps) => {
  const { data: countryList = [] } = useRequestCitiesCountries({});
  const getCoordinates = useGetCoordinates();
  const { showToast } = useToastMessage();
  const [coordinates, setCoordinates] = useState<{
    latitude: number | null;
    longitude: number | null;
  } | null>(null);
  const { t } = useTranslation();

  const { createLocation } = useCreateLocation();
  const { updateLocation } = useUpdateLocation();
  const { copyLocation } = useCopyLocation(currentLocation);

  const onLocationSave = async () => {
    const isEditing = !!currentLocation.uuid;

    if (isSubmitDisabled || hasValidationErrors) {
      setShowFormErrors(true);
      return;
    }

    setIsLoading(true);

    if (isCopying) {
      copyLocation(setIsLoading);
    }

    if (isEditing) {
      const {
        street,
        city,
        countryISOA2,
        zipCode,
        latitudeMap = 0,
        longitudeMap = 0,
      } = currentLocation;
      const country = countryList?.find((country) => country.code === countryISOA2) as any;
      const address = `${street}, ${zipCode} ${city}, ${country?.name}`;

      const { latitude, longitude } = await getCoordinates(address);

      const { isNearBy } = areCoordinatesClose(latitude, longitude, latitudeMap, longitudeMap, 100);
      const proceed =
        coordinates?.latitude &&
        coordinates?.longitude &&
        coordinates?.latitude === latitudeMap &&
        coordinates?.longitude === longitudeMap;

      if (!proceed || !coordinates) {
        if (!isNearBy) {
          showToast(
            generateUUID(),
            <Trans
              components={{
                b: <b />,
                p: <p />,
              }}
            >
              {t('app.location.locationEditSave.coordinatesChanged')}
            </Trans>,
            ToastVariant.WARNING,
            true,
            undefined,
            20000,
          );
          setIsLoading(false);
          setCoordinates({ latitude: latitudeMap, longitude: longitudeMap });
          return;
        }
      }
      currentLocation.latitude = latitudeMap;
      currentLocation.longitude = longitudeMap;
    }

    if (isEditing && !isCopying) {
      updateLocation({
        currentLocation,
        setIsLoading,
        setIsSubmitDisabled,
        setShowFormErrors,
      });
    }

    if (!isEditing && !isCopying) {
      createLocation({ currentLocation, setIsLoading });
    }

    setIsLoading(false);
  };

  return { onLocationSave };
};
